import React, {useEffect, useRef, useState} from 'react';
import {useNodeCallbackContext} from 'Contexts/NodeCallBackContext';
import {Outline} from 'Views/Preview/RichPreview/Highlighter/Highlighter';
import cloneDeep from 'lodash/cloneDeep';
import {AstView, findNodeById, PositionDeltas, applyPositionToNode, findParentOfNodeById, Snaps} from 'rendition-core';
import {useMousePosition} from 'Hooks/useMousePosition';
import {useKeyBind} from 'Hooks/useKeyBind';
import {useViewStore} from 'stores/view';
import {HorizontalSnapGuide, VerticalSnapGuide} from './SnapGuide';
import {useViewContext} from 'Contexts/ViewContext';

type Props = {
    nodeId: string | null;
    setRecordHistory: (record: boolean) => void;
    clearResize: () => void;
};

type DragStartLocation = 'Top' | 'Bottom' | 'Left' | 'Right' | 'Middle' | 'XMove' | 'YMove';
type XY = {x: number; y: number};

export const Resizer: React.FC<React.PropsWithChildren<Props>> = ({nodeId, setRecordHistory, clearResize}) => {
    const ref = useRef<HTMLDivElement | null>(null);

    return (
        <div ref={ref} style={{position: 'relative', pointerEvents: 'none'}}>
            {nodeId && (
                <NodeResizeControl
                    setRecordHistory={setRecordHistory}
                    nodeId={nodeId}
                    anchorRef={ref}
                    clearResize={clearResize}
                />
            )}
        </div>
    );
};

const NodeResizeControl: React.FC<
    React.PropsWithChildren<Props & {anchorRef: React.MutableRefObject<HTMLDivElement | null>; nodeId: string}>
> = ({nodeId, anchorRef, setRecordHistory, clearResize}) => {
    const {view, setView} = useViewContext();
    const [snaps, setSnaps] = useState<Snaps>({x: [], y: []});
    const dragStartingViewState = useRef<AstView | null>(null);
    const mouseStart = useRef<{x: number; y: number}>({x: 0, y: 0});
    const node = findNodeById(view.tree, nodeId);
    const parent = findParentOfNodeById(view.tree, nodeId);
    const [dragSide, setDragSide] = useState<DragStartLocation[]>([]);
    const setPreviewSize = useViewStore.use.setPreviewWidth();

    const moveOnly = false;
    const noMove = true;

    // on start adjust preview quickly.

    const getCurrentPreviewLocation = () => {
        if (anchorRef.current) {
            const rec = anchorRef.current.getBoundingClientRect();
            return {x: rec.left, y: rec.top};
        }
        return {x: 0, y: 0};
    };

    useEffect(() => {
        // set PreviewSize
        setPreviewSize(view.tree.position.width);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [nodeId]);

    const {nodeCallbacks, scaleRef} = useNodeCallbackContext();

    const mousePosition = useMousePosition();

    useEffect(() => {
        const onMouseMoveDrag = () => {
            if (!dragSide) {
                return;
            }
            setRecordHistory(false);
            const delta = calcMove(mouseStart.current, mousePosition.current);
            updateNode(delta);
        };

        const updateNode = (unScaledDelta: XY) => {
            if (!nodeId || dragSide.length === 0 || !dragStartingViewState.current) {
                return;
            }

            const mouseDelta = {x: unScaledDelta.x / scaleRef.current, y: unScaledDelta.y / scaleRef.current};

            const deltas: PositionDeltas = {
                widthDelta: 0,
                heightDelta: 0,
                absoluteXDelta: 0,
                absoluteYDelta: 0,
            };

            if (dragSide.includes('Left')) {
                deltas.widthDelta = mouseDelta.x;
                deltas.absoluteXDelta = -mouseDelta.x;
            }
            if (dragSide.includes('Right')) {
                deltas.widthDelta = -mouseDelta.x;
            }
            if (dragSide.includes('Top')) {
                deltas.heightDelta = mouseDelta.y;
                deltas.absoluteYDelta = -mouseDelta.y;
            }
            if (dragSide.includes('Bottom')) {
                deltas.heightDelta = -mouseDelta.y;
            }
            if (dragSide.includes('Middle')) {
                deltas.absoluteYDelta = -mouseDelta.y;
                deltas.absoluteXDelta = -mouseDelta.x;
            }
            if (dragSide.includes('XMove')) {
                deltas.absoluteXDelta = -mouseDelta.x;
            }
            if (dragSide.includes('YMove')) {
                deltas.absoluteYDelta = -mouseDelta.y;
            }

            if (moveOnly) {
                deltas.heightDelta = 0;
                deltas.widthDelta = 0;
            }

            const newView = applyPositionToNode(dragStartingViewState.current, nodeId, deltas, snaps);
            const newNodeVersion = findNodeById(newView.tree, nodeId);
            if (newNodeVersion && !newNodeVersion.userInput.lockChildren) {
                newNodeVersion.userInput.lockChildren = true;
            }
            if (newNodeVersion) {
                newNodeVersion.meta.systemGenerated = false;
            }

            setView(newView);
        };

        const updateMousePosition = (ev: MouseEvent) => {
            onMouseMoveDrag();
        };
        const onMouseUp = () => {
            if (node) {
                node.userInput.lockChildren = false;
            }
            setView({...view});
            setRecordHistory(true);
            setDragSide([]);
        };
        if (dragSide.length > 0) {
            window.addEventListener('mousemove', updateMousePosition);
            window.addEventListener('mouseup', onMouseUp);
            window.addEventListener('blur', onMouseUp);
        }

        return () => {
            window.removeEventListener('mousemove', updateMousePosition);
            window.removeEventListener('mouseup', onMouseUp);
            window.removeEventListener('blur', onMouseUp);
        };
    }, [dragSide, nodeId, node, mousePosition, view, setView, setRecordHistory, moveOnly, scaleRef, snaps]);

    const adjustNodeByPixel = (e: Mousetrap.ExtendedKeyboardEvent, deltaX: number, deltaY: number) => {
        e.preventDefault();
        if (node) {
            node.position.absoluteX += deltaX;
            node.position.absoluteY += deltaY;
        }
        setView({...view});
    };

    useKeyBind('left', (e) => adjustNodeByPixel(e, -1, 0));
    useKeyBind('right', (e) => adjustNodeByPixel(e, 1, 0));
    useKeyBind('up', (e) => adjustNodeByPixel(e, 0, -1));
    useKeyBind('down', (e) => adjustNodeByPixel(e, 0, 1));
    useKeyBind('enter', clearResize);
    useKeyBind('esc', clearResize);

    if (!nodeId || !nodeCallbacks[nodeId] || !node) {
        return null;
    }

    const xy = getCurrentPreviewLocation();

    const onDragStart = (dragLocation: DragStartLocation[]) => {
        dragStartingViewState.current = cloneDeep(view);
        mouseStart.current = {...mousePosition.current};

        // const siblings = parent?.children.filter((sibling) => sibling.internalId !== nodeId && !sibling.calculatedLayoutAbstraction.absolutePosition) || [];
        // const siblingXGuides = uniq(
        //     siblings
        //         .map((sibling) => [
        //             sibling.position.absoluteX,
        //             sibling.position.absoluteX + sibling.position.width,
        //         ])
        //         .flat()
        // );
        // const siblingYGuides = uniq(
        //     siblings
        //         .map((sibling) => [
        //             sibling.position.absoluteY,
        //             sibling.position.absoluteY + sibling.position.height,
        //         ])
        //         .flat()
        // );

        setSnaps({
            x: [
                parent?.innerPosition.absoluteX!,
                parent?.innerPosition.absoluteX! + parent?.innerPosition.width!,
                //       ...siblingXGuides,
            ],
            y: [
                parent?.innerPosition.absoluteY!,
                parent?.innerPosition.absoluteY! + parent?.innerPosition.height!,
                //     ...siblingYGuides,
            ],
        });

        setDragSide(dragLocation);
    };

    return (
        <>
            <Outline
                key={nodeId}
                border={''}
                zIndex={200}
                locationCallBack={nodeCallbacks[node.internalId]}
                rootXY={xy}
                pointerEvents
            >
                <GrabLine
                    onMouseDown={(e, placement) => {
                        e.stopPropagation();
                        e.preventDefault();
                        onDragStart(placement);
                    }}
                    placement="Left"
                />
                <GrabLine
                    onMouseDown={(e, placement) => {
                        e.stopPropagation();
                        e.preventDefault();
                        if (moveOnly) {
                            onDragStart(['XMove']);
                        } else {
                            onDragStart(['Right']);
                        }
                    }}
                    placement="Right"
                />
                <GrabLine
                    onMouseDown={(e, placement) => {
                        e.stopPropagation();
                        e.preventDefault();
                        onDragStart(placement);
                    }}
                    placement="Top"
                />
                <GrabLine
                    onMouseDown={(e, placement) => {
                        e.stopPropagation();
                        e.preventDefault();
                        if (moveOnly) {
                            onDragStart(['YMove']);
                        } else {
                            onDragStart(['Bottom']);
                        }
                    }}
                    placement="Bottom"
                />
                {!moveOnly && (
                    <>
                        <GrabCorner
                            onMouseDown={(e, placement) => {
                                e.stopPropagation();
                                e.preventDefault();
                                onDragStart(placement);
                            }}
                            placement={['Bottom', 'Left']}
                        />
                        <GrabCorner
                            onMouseDown={(e, placement) => {
                                e.stopPropagation();
                                e.preventDefault();
                                onDragStart(placement);
                            }}
                            placement={['Bottom', 'Right']}
                        />
                        <GrabCorner
                            onMouseDown={(e, placement) => {
                                e.stopPropagation();
                                e.preventDefault();
                                onDragStart(placement);
                            }}
                            placement={['Top', 'Left']}
                        />
                        <GrabCorner
                            onMouseDown={(e, placement) => {
                                e.stopPropagation();
                                e.preventDefault();
                                onDragStart(placement);
                            }}
                            placement={['Top', 'Right']}
                        />
                    </>
                )}
                {!noMove && (
                    <div
                        onMouseDown={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            onDragStart(['Middle']);
                        }}
                        style={{
                            width: '100%',
                            height: '100%',
                            cursor: dragSide.includes('Middle') ? 'grabbing' : ' grab',
                        }}
                    />
                )}
            </Outline>
            {snaps.x.map((xPos) => (
                <VerticalSnapGuide
                    view={view}
                    scaleRef={scaleRef}
                    locationCallBack={nodeCallbacks[view.tree.internalId]}
                    rootXY={xy}
                    xPos={xPos}
                />
            ))}
            {snaps.y.map((yPos) => (
                <HorizontalSnapGuide
                    view={view}
                    scaleRef={scaleRef}
                    locationCallBack={nodeCallbacks[view.tree.internalId]}
                    rootXY={xy}
                    yPos={yPos}
                />
            ))}
        </>
    );
};

const calcMove: (start: XY, end: XY) => XY = (start: XY, end: XY) => {
    return {
        x: start.x - end.x,
        y: start.y - end.y,
    };
};

const BORDER_SIZE = 2;
const HIT_BOX_PADDING = 5;

const AddedCss = {
    Left: {
        marginLeft: -(HIT_BOX_PADDING + BORDER_SIZE),
        marginTop: -HIT_BOX_PADDING,
        cursor: 'ew-resize',
    },
    Right: {
        marginRight: -(HIT_BOX_PADDING + BORDER_SIZE),
        marginTop: -HIT_BOX_PADDING,
        right: 0,
        cursor: 'ew-resize',
    },
    Top: {
        marginLeft: -HIT_BOX_PADDING,
        marginTop: -(HIT_BOX_PADDING + BORDER_SIZE),
        cursor: 'ns-resize',
    },
    Bottom: {
        marginLeft: -HIT_BOX_PADDING,
        marginBottom: -(HIT_BOX_PADDING + BORDER_SIZE),
        bottom: 0,
        cursor: 'ns-resize',
    },
};

const GrabLine: React.FC<
    React.PropsWithChildren<{
        placement: DragStartLocation;
        onMouseDown: (e: React.MouseEvent, DragStartLocation: DragStartLocation[]) => void;
    }>
> = ({onMouseDown, placement}) => {
    const direction = placement === 'Bottom' || placement === 'Top' ? 'horizontal' : 'vertical';

    const placementCss = AddedCss[placement];

    return (
        <div
            style={{
                pointerEvents: 'auto',
                position: 'absolute',
                padding: HIT_BOX_PADDING,
                width: direction === 'vertical' ? BORDER_SIZE : '100%',
                height: direction === 'vertical' ? '100%' : BORDER_SIZE,
                ...placementCss,
            }}
            onMouseDown={(e) => onMouseDown(e, [placement])}
        >
            <div
                style={{
                    //backgroundColor: 'orange',
                    width: direction === 'vertical' ? BORDER_SIZE : '100%',
                    height: direction === 'vertical' ? '100%' : BORDER_SIZE,
                }}
            ></div>
        </div>
    );
};

const CORNER_RADIUS = 4;
const CORNER_MARGIN_CORRECT = -(HIT_BOX_PADDING + BORDER_SIZE + CORNER_RADIUS);

const CornerPlacementCss = {
    'Top Left': {
        top: 0,
        marginLeft: CORNER_MARGIN_CORRECT,
        marginTop: CORNER_MARGIN_CORRECT,
        cursor: 'nwse-resize',
    },
    'Top Right': {
        top: 0,
        right: 0,
        marginRight: CORNER_MARGIN_CORRECT,
        marginTop: CORNER_MARGIN_CORRECT,
        cursor: 'nesw-resize',
    },
    'Bottom Left': {
        bottom: 0,
        marginLeft: CORNER_MARGIN_CORRECT,
        marginBottom: CORNER_MARGIN_CORRECT,
        cursor: 'nesw-resize',
    },
    'Bottom Right': {
        bottom: 0,
        right: 0,
        marginRight: CORNER_MARGIN_CORRECT,
        marginBottom: CORNER_MARGIN_CORRECT,
        cursor: 'nwse-resize',
    },
};

const GrabCorner: React.FC<
    React.PropsWithChildren<{
        placement: DragStartLocation[];
        onMouseDown: (e: React.MouseEvent, DragStartLocation: DragStartLocation[]) => void;
    }>
> = ({onMouseDown, placement}) => {
    const cornerPlacementCss = CornerPlacementCss[placement.join(' ')];

    return (
        <div
            style={{
                pointerEvents: 'auto',
                position: 'absolute',
                padding: HIT_BOX_PADDING,
                ...cornerPlacementCss,
            }}
            onMouseDown={(e) => onMouseDown(e, placement)}
        >
            <div
                style={{
                    backgroundColor: 'white',
                    width: CORNER_RADIUS * 2,
                    height: CORNER_RADIUS * 2,
                    border: `${BORDER_SIZE}px solid orange`,
                }}
            ></div>
        </div>
    );
};
